import { useEffect, useRef } from "react"
import { z } from "zod"
import { addInvitationByRef, extractRefCode } from "@/entities/invitation"
import { HttpError } from "@/shared/api/errors"
import {
	isInstallReferrerAvailable,
	readInstallReferrer,
} from "@/shared/lib/install-referrer"
import {
	clearClipboard,
	detectPlatform,
	getNativeDeviceInfo,
	onAppActive,
	readFromClipboard,
} from "@/shared/lib/platform"
import {
	createDeviceStorageItem,
	createSessionStorageItem,
} from "@/shared/lib/storage"
import { useLaunchParams } from "@/shared/lib/telegram"
import { REFERRAL_SERVICE_URL } from "../lib/constants"

const platform = detectPlatform()

// Код, который сервер уже принял: install-источники больше не спрашиваем.
const resolvedCode = createDeviceStorageItem<string | null>({
	key: "referral-resolved",
	schema: z.string().nullable(),
	fallback: null,
})

// Найденный код до подтверждения сервером: буфер и визит fingerprint гасятся
// раньше, чем приходит ответ.
const pendingCode = createSessionStorageItem<string | null>({
	key: "referral-pending",
	schema: z.string().nullable(),
	fallback: null,
})

// Пересёкшийся вызов не выбрасываем — иначе свежий код из буфера потеряется, —
// а ставим в очередь на повтор.
let inFlight = false
let queued: boolean | null = null

// "ref_" + 12 hex — формат генератора приглашений, одинаковый в social-service
// (RandomRefCode) и в легаси-Python (uuid4().hex[:12]).
function looksLikeRefCode(value: string): boolean {
	return /^ref_[0-9a-f]{8,32}$/.test(value)
}

function parseRefCode(referrerUrl: string): string | null {
	const code = new URLSearchParams(referrerUrl).get("tg_ref_code")
	if (code && looksLikeRefCode(code)) return code
	return null
}

// Приглашения с таким кодом нет — повторять нечего. Всё остальное, включая
// 401, 408, 429 и 5xx, временное: код оставляем на следующий заход.
function isCodeRejected(error: unknown): boolean {
	if (!(error instanceof HttpError)) return false
	return error.status === 400 || error.status === 404 || error.status === 409
}

// Буфер чистим сразу: успешный POST через несколько секунд затёр бы то, что
// пользователь скопировал тем временем. Код переживает это в pending-ключе.
async function takeInviteCodeFromClipboard(): Promise<string | null> {
	const value = await readFromClipboard()
	if (!value || !looksLikeRefCode(value)) return null

	pendingCode.set(value)
	await clearClipboard()
	return value
}

async function readInviteCodeFromInstallReferrer(): Promise<string | null> {
	if (!isInstallReferrerAvailable()) return null
	try {
		return parseRefCode(await readInstallReferrer())
	} catch {
		return null
	}
}

async function resolveFingerprint(
	nativePlatform: "ios" | "android",
): Promise<string | null> {
	try {
		const device = await getNativeDeviceInfo()
		if (!device) return null
		const osMajorVersion = device.osVersion.split(".")[0]

		const res = await fetch(`${REFERRAL_SERVICE_URL}/invite/resolve`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				platform: nativePlatform,
				os_major_version: osMajorVersion,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				locale: navigator.language.split(/[-_]/)[0],
			}),
		})

		if (!res.ok) return null
		const data = (await res.json()) as { found: boolean; ref_code?: string }
		if (data.found && data.ref_code) {
			return data.ref_code
		}
		return null
	} catch {}
	return null
}

// Буфер — повторяемый канал: приглашение приходит и тому, у кого приложение уже
// стоит. Install referrer и fingerprint отвечают, кто привёл установку, и потому
// одноразовы — их спрашиваем только при checkInstallSources.
async function resolveOnce(
	nativePlatform: "ios" | "android",
	checkInstallSources: boolean,
): Promise<void> {
	const processedCode = resolvedCode.get()

	// Незавершённая прошлая попытка идёт первой: этот код уже забран из буфера.
	let code = pendingCode.get() ?? (await takeInviteCodeFromClipboard())

	if (!code) {
		if (!checkInstallSources || processedCode) return
		code = await readInviteCodeFromInstallReferrer()
		if (!code) code = await resolveFingerprint(nativePlatform)
	}

	if (!code || code === processedCode) {
		pendingCode.remove()
		return
	}

	// Держим код до подтверждения: визит fingerprint сервер гасит сразу и второй
	// раз его не отдаст.
	pendingCode.set(code)

	try {
		await addInvitationByRef(code)
	} catch (error) {
		console.error("Failed to process native referral:", error)
		// Код отвергнут — выкидываем, но install-источники не закрываем:
		// настоящее приглашение может прийти оттуда.
		if (isCodeRejected(error)) {
			pendingCode.remove()
		}
		return
	}

	resolvedCode.set(code)
	pendingCode.remove()
}

function resolveNativeReferral(
	nativePlatform: "ios" | "android",
	checkInstallSources: boolean,
): void {
	if (inFlight) {
		queued = (queued ?? false) || checkInstallSources
		return
	}
	inFlight = true

	void (async () => {
		try {
			let next: boolean | null = checkInstallSources
			while (next !== null) {
				queued = null
				await resolveOnce(nativePlatform, next)
				next = queued
			}
		} finally {
			inFlight = false
			queued = null
		}
	})()
}

export function useHandleInvitation(userId: number | undefined): void {
	const { tgWebAppStartParam } = useLaunchParams()
	const refCode = extractRefCode(tgWebAppStartParam)
	const processedRef = useRef<string | null>(null)

	useEffect(() => {
		if (!userId) return

		if (platform === "telegram") {
			if (!refCode || processedRef.current === refCode) return
			processedRef.current = refCode

			addInvitationByRef(refCode).catch((error) => {
				processedRef.current = null
				console.error("Failed to add invitation by ref:", error)
			})
			return
		}

		if (platform !== "ios" && platform !== "android") return

		resolveNativeReferral(platform, true)

		// Возврат из фона — второй шанс поймать буфер: по ссылке могли перейти,
		// пока приложение висело в фоне. Только Android — на iOS 16+ чтение
		// буфера показывает системный запрос «Вставить?».
		if (platform !== "android") return

		return onAppActive(() => {
			resolveNativeReferral(platform, false)
		})
	}, [userId, refCode])
}
